import CaptionToggleButton from "@components/controls/CaptionToggleButton";
import toggleSignature from "@core-ui/toggleSignature";
import t from "@ext/localization/locale/translate";
import type { Node } from "@tiptap/pm/model";
import { Field } from "@ui-kit/Field";
import { GlassToolbarButton, GlassToolbarIcon } from "@ui-kit/GlassToolbar";
import { PopoverInput } from "@ui-kit/Input";
import { Popover, PopoverContent, PopoverTrigger } from "@ui-kit/Popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@ui-kit/Tooltip";
import type { Dispatch, ReactElement, RefObject, SetStateAction } from "react";

interface VideoActionsProps {
	hasSignature: boolean;
	signatureRef: RefObject<HTMLInputElement>;
	updateAttributes: (attrs: Record<string, string>) => void;
	node: Node;
	setHasSignature: Dispatch<SetStateAction<boolean>>;
}

const VideoActions = (props: VideoActionsProps): ReactElement => {
	const { node, hasSignature, setHasSignature, signatureRef, updateAttributes } = props;
	const linkTooltip = t("editor.video.link");
	const originalTooltip = t("goto-original");
	const signatureTooltip = t("signature");

	const addSignature = () => {
		setHasSignature((prev) => toggleSignature(prev, signatureRef.current, updateAttributes));
	};

	const onChange = (value: string) => {
		updateAttributes({ path: value });
	};

	return (
		<>
			<Popover>
				<Tooltip>
					<TooltipTrigger asChild>
						<PopoverTrigger asChild>
							<GlassToolbarButton aria-label={linkTooltip} focusable>
								<GlassToolbarIcon icon="link" />
							</GlassToolbarButton>
						</PopoverTrigger>
					</TooltipTrigger>
					<TooltipContent sideOffset={2}>{linkTooltip}</TooltipContent>
				</Tooltip>
				<PopoverContent align="start" className="w-80 max-w-[calc(100vw-1rem)] p-3" side="top" sideOffset={8}>
					<Field
						control={({ id }) => (
							<PopoverInput
								autoCapitalize="none"
								defaultValue={node.attrs.path}
								id={id}
								onChange={onChange}
								placeholder="https://youtu.be/..."
								spellCheck={false}
								type="url"
							/>
						)}
						description={t("editor.video.link-description")}
						layout="vertical"
						title={linkTooltip}
					/>
				</PopoverContent>
			</Popover>
			{node.attrs.path && (
				<GlassToolbarButton aria-label={originalTooltip} asChild focusable tooltipText={originalTooltip}>
					<a href={node.attrs.path} rel="noreferrer" target="_blank">
						<GlassToolbarIcon icon="external-link" />
					</a>
				</GlassToolbarButton>
			)}
			<CaptionToggleButton
				active={hasSignature}
				caption={node.attrs.title}
				onToggle={addSignature}
				tooltipText={signatureTooltip}
			/>
		</>
	);
};

export default VideoActions;
